// In dev, use a relative path so requests go through the Vite proxy
// (see vite.config.ts) and stay same-origin, avoiding CORS. In production
// the app calls the deployed API directly.
const API_BASE = import.meta.env.DEV
  ? '/api'
  : 'https://ofp3lkrwq9.execute-api.us-east-1.amazonaws.com/api'

import localPostRaw from './localPost.json'
import aglfPostRaw from './aglfPost.json'

// Posts authored in the repo rather than the CMS. They are merged into the
// first page of results (newest first) and served directly by slug.
const LOCAL_POSTS = ([aglfPostRaw, localPostRaw] as unknown as BlogPost[]).sort(
  (a, b) => Date.parse(b.publishedAt || b.createdAt) - Date.parse(a.publishedAt || a.createdAt),
)

function localPostMatches(post: BlogPost, params?: { q?: string; tags?: string }): boolean {
  if (params?.q && !post.title.toLowerCase().includes(params.q.toLowerCase())) return false
  if (params?.tags) {
    const wanted = params.tags.split(',').map((t) => t.trim().toLowerCase()).filter(Boolean)
    if (wanted.length && !post.tags.some((t) => wanted.includes(t.toLowerCase()))) return false
  }
  return true
}

/** A single gallery image once normalised out of whatever shape the CMS sent. */
export interface BlogImage {
  url: string
  caption?: string
}

/**
 * An entry in a CMS image array. Editors that upload several files at once
 * serialise them either as plain URL strings or as objects, and the key used
 * for the URL varies by upload widget (Cloudinary returns `secure_url`, most
 * hand-rolled admin forms use `url`). Accept all of them rather than betting
 * on one.
 */
type RawBlogImage =
  | string
  | {
      url?: string
      src?: string
      secure_url?: string
      imageUrl?: string
      caption?: string
      alt?: string
      title?: string
    }

export interface BlogPost {
  id: string
  title: string
  slug: string
  image: string
  imagePublicId?: string
  banner?: string
  bannerPublicId?: string
  audio?: string // Optional link to an audio track
  description: string
  abstract?: string
  author?: string
  body: string
  tags: string[]
  status: string
  publishedAt?: string
  seoKeywords?: string[]
  createdAt: string
  updatedAt: string
  /**
   * Extra images attached to the post beyond the card thumbnail (`image`) and
   * the hero (`banner`). The public API does not return these yet — it exposes
   * only `image` and `banner` — so these are optional and every consumer must
   * tolerate their absence. Both key names are read because the CMS and the
   * public API have historically disagreed on naming.
   */
  images?: RawBlogImage[]
  gallery?: RawBlogImage[]
}

function toBlogImage(raw: RawBlogImage): BlogImage | null {
  if (typeof raw === 'string') {
    const url = raw.trim()
    return url ? { url } : null
  }
  if (!raw || typeof raw !== 'object') return null
  const url = (raw.url || raw.src || raw.secure_url || raw.imageUrl || '').trim()
  if (!url) return null
  const caption = (raw.caption || raw.alt || raw.title || '').trim()
  return caption ? { url, caption } : { url }
}

/**
 * Normalise a post's extra images into a clean, de-duplicated list.
 *
 * Drops anything already shown elsewhere on the detail page — the hero
 * (`banner`, falling back to `image`) and the card thumbnail — so a CMS that
 * includes the hero in its image array doesn't render it twice.
 */
export function getPostGallery(post: BlogPost): BlogImage[] {
  const raw = [...(post.images ?? []), ...(post.gallery ?? [])]
  const alreadyShown = new Set([post.banner, post.image].filter(Boolean) as string[])
  const seen = new Set<string>()
  const out: BlogImage[] = []

  for (const entry of raw) {
    const img = toBlogImage(entry)
    if (!img) continue
    if (alreadyShown.has(img.url) || seen.has(img.url)) continue
    seen.add(img.url)
    out.push(img)
  }

  return out
}

export interface BlogListResponse {
  data: BlogPost[]
  total: number
  page: number
  pageSize: number
  totalPages: number
}

// ─── In-memory cache ───────────────────────────────────────────────────────
// Keeps responses cached for 5 minutes so repeat visits to Insights/detail
// pages are instantaneous without hitting the API again.
// (Busting cache for audio feature update)
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes
const cache = new Map<string, { data: unknown; timestamp: number }>()

function getCached<T>(key: string): T | null {
  if (import.meta.env.DEV) return null // Disable caching in development mode
  const entry = cache.get(key)
  if (!entry) return null
  if (Date.now() - entry.timestamp > CACHE_TTL) {
    cache.delete(key)
    return null
  }
  return entry.data as T
}

function setCache(key: string, data: unknown) {
  cache.set(key, { data, timestamp: Date.now() })
}

// ─── API functions ─────────────────────────────────────────────────────────

export async function getPosts(params?: {
  page?: number
  pageSize?: number
  tags?: string
  author?: string
  q?: string
}): Promise<BlogListResponse> {
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize))
  if (params?.tags) searchParams.set('tags', params.tags)
  if (params?.author) searchParams.set('author', params.author)
  if (params?.q) searchParams.set('q', params.q)

  const url = `${API_BASE}/public/posts${searchParams.toString() ? '?' + searchParams.toString() : ''}`
  const cacheKey = `list:${searchParams.toString()}`

  // Return cached if available
  const cached = getCached<BlogListResponse>(cacheKey)
  if (cached) return cached

  const res = await fetch(url)
  if (!res.ok) throw new Error('Failed to fetch posts')
  const data: BlogListResponse = await res.json()
  
  // Inject repo-authored posts at the top of the first page only, so they
  // don't repeat on every page. Skip any the CMS already returns.
  if (!params?.page || params.page === 1) {
    const apiSlugs = new Set(data.data.map((p) => p.slug))
    const extras = LOCAL_POSTS.filter((p) => !apiSlugs.has(p.slug) && localPostMatches(p, params))
    data.data = [...extras, ...data.data]
    data.total += extras.length
  }
  setCache(cacheKey, data)

  // Pre-cache individual posts from the list response so detail pages load
  // instantly. Only cache entries that are actually complete: the detail page
  // renders post.body and post.tags without guards, so seeding the cache with a
  // trimmed list item would make /insights/:slug throw. The list endpoint
  // currently returns full posts, but this keeps a future API change from
  // turning into a blank article page.
  data.data.forEach((post) => {
    if (typeof post?.body === 'string' && Array.isArray(post?.tags)) {
      setCache(`post:${post.slug}`, post)
    }
  })

  return data
}

export async function getPostBySlug(slug: string): Promise<BlogPost> {
  const cacheKey = `post:${slug}`

  // Return cached if available (often pre-filled by getPosts)
  const cached = getCached<BlogPost>(cacheKey)
  if (cached) return cached

  const local = LOCAL_POSTS.find((p) => p.slug === slug)
  if (local) return local

  const res = await fetch(`${API_BASE}/public/posts/${slug}`)
  if (!res.ok) throw new Error('Post not found')
  const data: BlogPost = await res.json()
  setCache(cacheKey, data)
  return data
}

// ─── Prefetch ──────────────────────────────────────────────────────────────
// Call this early (e.g. on app mount or homepage) to warm the cache
// so the Insights page loads data instantly when navigated to.
let prefetchStarted = false

export function prefetchPosts() {
  if (prefetchStarted) return
  prefetchStarted = true
  // Fire and forget — non-blocking background fetch
  getPosts({ page: 1, pageSize: 12 }).catch(() => {
    // Silent fail — user will still fetch on demand
    prefetchStarted = false
  })
}
